// cspell:ignore brokername -- the variable as legacy fills it in the confirmation mails (Email sendApplicationVisitRequest)
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  OPTIONAL_PLACEHOLDERS,
  REQUIRED_PLACEHOLDERS,
} from "../VisitReminderSmsTexts/VisitReminderSmsTexts.utils";

/**
 * The variables an office may put in each text on this screen, shown in the
 * box beside it (DEV-3553, Jonas 2026-10-30): only the ones the backend that
 * sends that text actually fills.
 *
 * - The step-change mails (visitor, not eligible, plan for visit, invited,
 *   waiting list, candidate tenant) are filled by the step-change modal
 *   (schema.propertyApplicationNotEligible.utils); invited also keeps the two
 *   link markers, candidate tenant its candidacy button (serverless).
 * - The booking reminder (serverless bookingReminderWorker) and the automatic
 *   waiting list (api-next scheduleWaitlistNotification) fill the same three
 *   names, the reminder its two link markers as well.
 * - The two confirmation mails are filled by legacy (Email
 *   sendApplicationVisitRequest), with the names their defaults use.
 * - The visit reminder SMS is filled by serverless (visitReminder.sms.utils).
 * - The invitation by email is filled by api-next
 *   (inviteApplicantNotification): there is no candidate yet, only an address
 *   to write to, so it names the colleague who sends it instead.
 * - The owner intro is filled by nothing, so it gets no box.
 *
 * A text whose sender changes what it fills has to change here too.
 */

export interface INotificationVariable {
  name: string;
  isRequired: boolean;
}

const toVariables = (names: string[], requiredNames: string[] = []) =>
  names.map(name => ({ name, isRequired: requiredNames.includes(name) }));

/** Filled by the backend that sends the mail: the reminder and the automatic waiting list. */
const BACKEND_FILLED = ["applicant", "address", "actor"];
/**
 * Filled by the step-change modal, which also knows each candidate's first
 * name (finishTenantFeedbackText).
 */
const STEP_MAIL = ["applicant", "firstname", "address", "actor"];
const LINKS = ["bookingUrl", "noInterestUrl"];
const CONFIRMATION_MAIL = ["firstname", "brokername", "address", "lastname"];
// Already on the names of the shared variable set (DEV-3577). New with that
// tab, so no stored text uses older names.
const INVITATION_MAIL = ["senderName", "propertyAddress", "officeName"];

export const VARIABLES_BY_TAB: {
  [tabKey: string]: INotificationVariable[] | undefined;
} = {
  invite_applicant: toVariables(INVITATION_MAIL),
  tenant_confirmation_mail: toVariables(CONFIRMATION_MAIL),
  candidacy_confirmation_mail: toVariables(CONFIRMATION_MAIL),
  visitor: toVariables(STEP_MAIL),
  not_eligible: toVariables(STEP_MAIL),
  plan_for_visit: toVariables(STEP_MAIL),
  invited: toVariables([...STEP_MAIL, ...LINKS]),
  booking_reminder: toVariables([...BACKEND_FILLED, ...LINKS]),
  visit_reminder_sms: toVariables(
    [...REQUIRED_PLACEHOLDERS, ...OPTIONAL_PLACEHOLDERS],
    REQUIRED_PLACEHOLDERS,
  ),
  candidate_tenant: toVariables([...STEP_MAIL, "candidacyUrl"]),
  waiting_list: toVariables(STEP_MAIL),
  auto_waiting_list: toVariables(BACKEND_FILLED),
};

/**
 * What a variable turns into, one line in the box. Literal keys, so the i18n
 * key check sees every one of them; the SMS ones are the SMS screen's own.
 */
export const getVariableDescription = (name: string) => {
  switch (name) {
    case "applicant":
      return getLocalizedText("tenant_profile.notification.variable.applicant");
    case "actor":
    case "brokername":
    case "officeName":
      return getLocalizedText("tenant_profile.notification.variable.actor");
    case "senderName":
      return getLocalizedText("tenant_profile.notification.variable.sender");
    case "firstname":
      return getLocalizedText("tenant_profile.notification.variable.firstname");
    case "lastname":
      return getLocalizedText("tenant_profile.notification.variable.lastname");
    case "bookingUrl":
      return getLocalizedText(
        "tenant_profile.notification.variable.booking_url",
      );
    case "noInterestUrl":
      return getLocalizedText(
        "tenant_profile.notification.variable.no_interest_url",
      );
    case "candidacyUrl":
      return getLocalizedText(
        "tenant_profile.notification.variable.candidacy_url",
      );
    case "address":
    case "propertyAddress":
      return getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.variable.address",
      );
    case "visitDate":
      return getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.variable.visit_date",
      );
    case "visitTime":
      return getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.variable.visit_time",
      );
    case "contactName":
      return getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.variable.contact_name",
      );
    case "contactEmail":
      return getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.variable.contact_email",
      );
    case "officePhone":
      return getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.variable.office_phone",
      );
    default:
      return undefined;
  }
};
